import { useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  AlertTriangle,
  CalendarDays,
  Check,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  Gift,
  Lock,
  Mail,
  MapPin,
  Package,
  Phone,
  Sparkles,
  Truck,
  Usb,
  X,
} from "lucide-react";
import { subscribeBookingSelection } from "@/lib/bookingSelectionBridge";
import { trackBookingEvent, trackBookingEventOnce } from "@/lib/bookingAnalytics";
import { trapTabKey } from "@/lib/focusTrap";

const BACKEND_URL = (process.env.REACT_APP_BACKEND_URL || "").replace(/\/$/, "");
const API = `${BACKEND_URL}/api`;
const POLICY_VERSION = "2026-08-19";
const DETAILS_FORM_ID = "telefonka-booking-details";
const MOBILE_SUMMARY_ID = "booking-mobile-summary-dialog";

const EXTRA_PRESENTATION = {
  hu: [
    { id: "usb", name: "Pendrive a hangüzenetekkel", icon: Usb },
    { id: "cards", name: "Telefonkártyák – 50 + 25 kérdés", icon: Gift },
    { id: "sign", name: "Dekorációs használati tábla", icon: Package },
    { id: "extra-day", name: "Hosszabb bérlés – plusz 1 nap", icon: CalendarDays },
  ],
  en: [
    { id: "usb", name: "USB drive delivery", icon: Usb },
    { id: "cards", name: "Prompt cards – 50 + 25 questions", icon: Gift },
    { id: "sign", name: "Decorative instruction sign", icon: Package },
    { id: "extra-day", name: "Extended rental – one extra day", icon: CalendarDays },
  ],
};

const GIFTS = {
  hu: [
    { name: "Országos oda-vissza futár", icon: Truck },
    { name: "5 db Telefonka kérdéskártya", icon: Gift },
    { name: "Egyedi üdvözlőüzenet", icon: Sparkles },
  ],
  en: [
    { name: "Nationwide return courier", icon: Truck },
    { name: "5 Telefonka prompt cards", icon: Gift },
    { name: "Personal welcome message", icon: Sparkles },
  ],
};

const COPY = {
  hu: {
    steps: ["Időpont", "Telefonka", "Csomag / Extrák", "Adatok és összegzés"],
    title: "Négy egyszerű lépés, és kész",
    calendarTitle: "Mikor lesz az esemény?",
    calendarBody: "Válaszd ki a dátumot, és megmutatjuk, melyik Telefonka érhető el.",
    selectedDate: "Kiválasztott időpont",
    previousMonth: "Előző hónap",
    nextMonth: "Következő hónap",
    phoneTitle: "Válaszd ki a Telefonkádat",
    phoneBody: "Ha mindkettő elérhető, akár mindkét készüléket egyszerre is kiválaszthatod.",
    available: "Elérhető ezen a napon",
    booked: "Ezen a napon már foglalt.",
    choose: "Kiválasztás",
    chosen: "Kiválasztva",
    bothHint: "A két készülék egy foglalásban, egy Stripe fizetéssel rendelhető meg.",
    extrasTitle: "Tedd még személyesebbé",
    extrasBody: "Az ajándékok az árban vannak, ezeken felül választhatsz fizetős extrákat.",
    giftsTitle: "Ezeket ajándékba adjuk",
    extrasPaid: "További választható extrák",
    noExtras: "Nem kérek fizetős extrát",
    noExtrasSummary: "Nem választottál fizetős extrát.",
    detailsTitle: "Már csak néhány adat hiányzik",
    detailsBody: "A foglalás és a számlázás adatai.",
    detailsSection: "A. Foglalási és számlázási adatok",
    summarySection: "B. Foglalásod összegzése",
    name: "Teljes név",
    email: "E-mail-cím",
    phone: "Telefonszám",
    location: "Esemény helyszíne vagy települése",
    billingTitle: "Számlázási adatok",
    billingName: "Számlázási név / cégnév",
    country: "Ország",
    postal: "Irányítószám",
    city: "Település",
    address: "Cím",
    tax: "Adószám (opcionális)",
    note: "Megjegyzés vagy különleges kérés",
    consent: "Hozzájárulok, hogy a Telefonka a foglalásommal kapcsolatban felvegye velem a kapcsolatot.",
    terms: "Elolvastam és elfogadom az Általános Szerződési Feltételeket és az Adatkezelési Tájékoztatót.",
    summary: "Foglalásod összegzése",
    summaryToggle: "Foglalás részletei",
    closeSummary: "Foglalás részleteinek bezárása",
    viewDetails: "Részletek megtekintése",
    date: "Időpont",
    devices: "Telefonka",
    package: "Csomag",
    base: "Telefonka alapár",
    extras: "Fizetős extrák",
    included: "Ajándékba jár",
    total: "Fizetendő végösszeg",
    payable: "Fizetendő",
    back: "Vissza",
    edit: "Módosítás",
    gift: "Ajándék",
    loadingAvailability: "Elérhetőség ellenőrzése…",
    availabilityError: "Nem sikerült ellenőrizni az elérhetőséget. Kérjük, próbáld újra.",
    retry: "Újrapróbálás",
    pickDate: "Válaszd ki az esemény dátumát.",
    pickDevice: "Válassz legalább egy elérhető Telefonkát.",
    checkMissing: "Néhány kötelező adat hiányzik. Az első hibás mezőhöz léptünk.",
    reviewBeforePayment: "Biztonságos online fizetés Stripe-on keresztül.",
    stripeNext: "A fizetés a Stripe biztonságos fizetési felületén történik.",
    stripeTrust: "Bankkártyás fizetés. Az elérhető gyorsfizetési lehetőségeket a Stripe a fizetési oldalon jeleníti meg.",
    priceChanged: "A foglalás ára időközben frissült. Frissítettük az összeget; kérjük, ellenőrizd újra a részleteket.",
    priceVerificationFailed: "Az aktuális árat nem sikerült biztonságosan egyeztetni. A fizetés nem indult el; kérjük, próbáld újra.",
    pay: "Tovább a biztonságos fizetéshez",
    paying: "Fizetés előkészítése…",
    cta: ["Telefonka kiválasztása", "Tovább a csomagokhoz", "Adatok megadása", "Tovább a fizetéshez"],
    conflict: "A kiválasztott Telefonka időközben foglalttá vált. Frissítettük az elérhetőséget.",
    generic: "Valami hiba történt. Kérjük, próbáld újra.",
    required: "Ezt a mezőt kérjük, töltsd ki.",
    requiredMessages: {
      customer_name: "Add meg a neved.",
      customer_email: "Add meg az e-mail-címed.",
      customer_phone: "Add meg a telefonszámod.",
      event_location: "Add meg az esemény helyszínét vagy települését.",
      billing_name: "Add meg a számlázási nevet vagy cégnevet.",
      billing_country: "Add meg a számlázási országot.",
      billing_postal_code: "Add meg a számlázási irányítószámot.",
      billing_city: "Add meg a számlázási települést.",
      billing_address: "Add meg a számlázási címet.",
    },
    requiredConsent: "A foglalás folytatásához járulj hozzá a kapcsolattartáshoz.",
    requiredTerms: "A foglalás folytatásához fogadd el a feltételeket.",
    invalidEmail: "Adj meg egy érvényes e-mail-címet.",
    invalidPhone: "Adj meg egy érvényes telefonszámot.",
  },
  en: {
    steps: ["Date", "Telefonka", "Package / Extras", "Details & summary"],
    title: "Four simple steps",
    calendarTitle: "When is your event?",
    calendarBody: "Choose the date and we will show which Telefonka is available.",
    selectedDate: "Selected date",
    previousMonth: "Previous month",
    nextMonth: "Next month",
    phoneTitle: "Choose your Telefonka",
    phoneBody: "If both are available, you can select both devices in one booking.",
    available: "Available on this date",
    booked: "Already booked on this date.",
    choose: "Select",
    chosen: "Selected",
    bothHint: "Both devices can be booked together with one Stripe payment.",
    extrasTitle: "Make it more personal",
    extrasBody: "Included gifts are free; optional paid extras can be added below.",
    giftsTitle: "Included gifts",
    extrasPaid: "Optional paid extras",
    noExtras: "No paid extras",
    noExtrasSummary: "You did not select any paid extras.",
    detailsTitle: "Just a few details left",
    detailsBody: "Booking and billing details.",
    detailsSection: "A. Booking and billing details",
    summarySection: "B. Your booking summary",
    name: "Full name",
    email: "Email address",
    phone: "Phone number",
    location: "Event location or city",
    billingTitle: "Billing details",
    billingName: "Billing name / company",
    country: "Country",
    postal: "Postal code",
    city: "City",
    address: "Address",
    tax: "Tax number (optional)",
    note: "Notes or special request",
    consent: "I agree that Telefonka may contact me about my booking.",
    terms: "I have read and accept the Terms and Conditions and Privacy Policy.",
    summary: "Booking summary",
    summaryToggle: "Booking details",
    closeSummary: "Close booking details",
    viewDetails: "View details",
    date: "Date",
    devices: "Telefonka",
    package: "Package",
    base: "Telefonka base price",
    extras: "Paid extras",
    included: "Included as a gift",
    total: "Amount due",
    payable: "Amount due",
    back: "Back",
    edit: "Edit",
    gift: "Gift",
    loadingAvailability: "Checking availability…",
    availabilityError: "We could not check availability. Please try again.",
    retry: "Try again",
    pickDate: "Choose an event date.",
    pickDevice: "Choose at least one available Telefonka.",
    checkMissing: "Some required details are missing. We moved to the first field that needs attention.",
    reviewBeforePayment: "Secure online payment through Stripe.",
    stripeNext: "Payment takes place on Stripe's secure payment page.",
    stripeTrust: "Card payment. Stripe shows the available express payment options on the payment page.",
    priceChanged: "The booking price has changed. We refreshed the total; please review the details again.",
    priceVerificationFailed: "We could not safely verify the current price. Payment was not started; please try again.",
    pay: "Continue to secure payment",
    paying: "Preparing payment…",
    cta: ["Choose Telefonka", "Continue to packages", "Enter your details", "Continue to payment"],
    conflict: "One of the selected Telefonkas has just been booked. Availability was refreshed.",
    generic: "Something went wrong. Please try again.",
    required: "Please complete this field.",
    requiredMessages: {
      customer_name: "Enter your name.",
      customer_email: "Enter your email address.",
      customer_phone: "Enter your phone number.",
      event_location: "Enter the event location or city.",
      billing_name: "Enter the billing name or company.",
      billing_country: "Enter the billing country.",
      billing_postal_code: "Enter the billing postal code.",
      billing_city: "Enter the billing city.",
      billing_address: "Enter the billing address.",
    },
    requiredConsent: "Agree to booking-related contact to continue.",
    requiredTerms: "Accept the terms to continue.",
    invalidEmail: "Enter a valid email address.",
    invalidPhone: "Enter a valid phone number.",
  },
};

const initialForm = {
  customer_name: "",
  customer_email: "",
  customer_phone: "",
  event_location: "",
  event_type: "",
  selected_package: "",
  billing_name: "",
  billing_country: "Magyarország",
  billing_postal_code: "",
  billing_city: "",
  billing_address: "",
  billing_tax_number: "",
  message: "",
};

const requiredFields = [
  "customer_name",
  "customer_email",
  "customer_phone",
  "event_location",
  "billing_name",
  "billing_country",
  "billing_postal_code",
  "billing_city",
  "billing_address",
];

const pad = (value) => String(value).padStart(2, "0");
const isoDate = (date) => `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
const startOfDay = (date) => new Date(date.getFullYear(), date.getMonth(), date.getDate());
const newRequestId = () => globalThis.crypto?.randomUUID?.() || `tk-${Date.now()}-${Math.random().toString(36).slice(2)}`;
const money = (value, lang) => `${new Intl.NumberFormat(lang === "hu" ? "hu-HU" : "en-GB").format(Number(value || 0))}${lang === "hu" ? " Ft" : " HUF"}`;
const dateLabel = (value, lang) => value ? new Intl.DateTimeFormat(lang === "hu" ? "hu-HU" : "en-GB", { year: "numeric", month: "long", day: "numeric", weekday: "long" }).format(new Date(`${value}T12:00:00`)) : "—";
const shortDateLabel = (value, lang) => value ? new Intl.DateTimeFormat(lang === "hu" ? "hu-HU" : "en-GB", { year: "numeric", month: "short", day: "numeric" }).format(new Date(`${value}T12:00:00`)) : "—";

function daysUntilEvent(value, baseDate) {
  const [year, month, day] = String(value || "").split("-").map(Number);
  if (![year, month, day].every(Number.isFinite)) return undefined;
  const target = Date.UTC(year, month - 1, day);
  const base = Date.UTC(baseDate.getFullYear(), baseDate.getMonth(), baseDate.getDate());
  return Math.max(0, Math.round((target - base) / 86400000));
}

function deviceAnalyticsProperties(devices = []) {
  const ids = devices.map((device) => String(device.id || "")).filter(Boolean);
  const types = devices.map((device) => String(device.type || "unknown"));
  return {
    device_count: devices.length,
    device_id: devices.length === 1 ? ids[0] : undefined,
    device_type: devices.length === 1 ? types[0] : "multiple",
    device_types: types,
  };
}

function calendarDays(cursor) {
  const first = new Date(cursor.getFullYear(), cursor.getMonth(), 1);
  const monday = (first.getDay() + 6) % 7;
  const start = new Date(first);
  start.setDate(first.getDate() - monday);
  return Array.from({ length: 42 }, (_, index) => {
    const day = new Date(start);
    day.setDate(start.getDate() + index);
    return day;
  });
}

function validationErrors(form, consent, terms, c) {
  const next = {};
  requiredFields.forEach((name) => {
    if (!String(form[name] || "").trim()) next[name] = c.requiredMessages?.[name] || c.required;
  });
  if (form.customer_email && !/^\S+@\S+\.\S+$/.test(form.customer_email)) next.customer_email = c.invalidEmail;
  const digits = form.customer_phone.replace(/\D/g, "");
  if (form.customer_phone && (digits.length < 7 || digits.length > 15)) next.customer_phone = c.invalidPhone;
  if (!consent) next.consent = c.requiredConsent;
  if (!terms) next.terms = c.requiredTerms;
  return next;
}

function mergeCatalogExtras(serverExtras, lang) {
  const presentation = EXTRA_PRESENTATION[lang] || EXTRA_PRESENTATION.hu;
  return (Array.isArray(serverExtras) ? serverExtras : []).map((extra) => {
    const id = String(extra.id || "");
    const display = presentation.find((item) => item.id === id);
    return {
      id,
      name: lang === "en" ? (display?.name || extra.name) : (extra.name || display?.name),
      price: Number(extra.price || 0),
      icon: display?.icon || Package,
    };
  });
}

function authoritativeCatalogTotal(catalog, selectedDeviceIds, selectedExtraIds) {
  const devices = Array.isArray(catalog?.devices) ? catalog.devices : [];
  const extras = Array.isArray(catalog?.extras) ? catalog.extras : [];
  const selectedDevices = selectedDeviceIds.map((id) => devices.find((device) => String(device.id) === String(id))).filter(Boolean);
  const selectedExtras = selectedExtraIds.map((id) => extras.find((extra) => String(extra.id) === String(id))).filter(Boolean);
  if (selectedDevices.length !== selectedDeviceIds.length || selectedExtras.length !== selectedExtraIds.length) return null;
  const total = selectedDevices.reduce((sum, device) => sum + Number(device.price || 0), 0)
    + selectedExtras.reduce((sum, extra) => sum + Number(extra.price || 0), 0);
  return { total, devices: selectedDevices, extras: selectedExtras };
}

export default function BookingFlowV2({ lang = "hu" }) {
  const c = COPY[lang] || COPY.hu;
  const extraPresentation = EXTRA_PRESENTATION[lang] || EXTRA_PRESENTATION.hu;
  const gifts = GIFTS[lang] || GIFTS.hu;
  const today = useMemo(() => startOfDay(new Date()), []);
  const [step, setStep] = useState(1);
  const [cursor, setCursor] = useState(() => new Date());
  const [eventDate, setEventDate] = useState("");
  const [devices, setDevices] = useState([]);
  const [catalogExtras, setCatalogExtras] = useState([]);
  const [selectedDeviceIds, setSelectedDeviceIds] = useState([]);
  const [selectedExtras, setSelectedExtras] = useState([]);
  const [form, setForm] = useState(initialForm);
  const [consent, setConsent] = useState(false);
  const [terms, setTerms] = useState(false);
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [availabilityError, setAvailabilityError] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [requestId, setRequestId] = useState(() => newRequestId());
  const [mobileSummary, setMobileSummary] = useState(false);
  const submitLock = useRef(false);
  const stepTopRef = useRef(null);
  const hasMountedRef = useRef(false);
  const mobileSummaryDialogRef = useRef(null);
  const mobileSummaryTriggerRef = useRef(null);
  const mobileSummaryPreviousFocusRef = useRef(null);

  const extrasCatalog = useMemo(() => mergeCatalogExtras(catalogExtras, lang), [catalogExtras, lang]);
  const selectedDevices = devices.filter((device) => selectedDeviceIds.includes(String(device.id)));
  const chosenExtras = extrasCatalog.filter((item) => selectedExtras.includes(item.id));
  const baseTotal = selectedDevices.reduce((sum, device) => sum + Number(device.price || 0), 0);
  const extrasTotal = chosenExtras.reduce((sum, item) => sum + item.price, 0);
  const total = baseTotal + extrasTotal;
  const detailsReady = Object.keys(validationErrors(form, consent, terms, c)).length === 0;
  const stepReady = step === 1 ? !!eventDate : step === 2 ? selectedDeviceIds.length > 0 && !loading : step === 3 ? true : detailsReady;

  const refreshAvailability = async (date = eventDate) => {
    if (!date) return;
    setLoading(true);
    setAvailabilityError(false);
    try {
      const [availability, catalog] = await Promise.all([
        axios.get(`${API}/availability`, { params: { date } }),
        axios.get(`${API}/booking-catalog`),
      ]);
      const availableIds = new Set((availability.data?.available_devices || []).map((item) => String(item.id)));
      const catalogDevices = Array.isArray(catalog.data?.devices) ? catalog.data.devices : [];
      const catalogExtraRows = Array.isArray(catalog.data?.extras) ? catalog.data.extras : [];
      if (catalogDevices.length === 0 || catalogExtraRows.length === 0) throw new Error("invalid_booking_catalog");
      const nextDevices = catalogDevices.map((device) => ({
        ...device,
        id: String(device.id),
        available: availableIds.has(String(device.id)),
      }));
      setDevices(nextDevices);
      setCatalogExtras(catalogExtraRows);
      setSelectedDeviceIds((current) => current.filter((id) => nextDevices.some((device) => device.id === id && device.available)));
      setSelectedExtras((current) => current.filter((id) => catalogExtraRows.some((extra) => String(extra.id) === id)));
    } catch (_error) {
      setAvailabilityError(true);
      setDevices([]);
      setCatalogExtras([]);
      toast.error(c.availabilityError);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!hasMountedRef.current) {
      hasMountedRef.current = true;
      return;
    }
    const prefersReducedMotion = window.matchMedia?.("(prefers-reduced-motion: reduce)")?.matches;
    stepTopRef.current?.scrollIntoView({ behavior: prefersReducedMotion ? "auto" : "smooth", block: "start" });
  }, [step]);

  useEffect(() => {
    if (!eventDate) {
      setDevices([]);
      setCatalogExtras([]);
      setSelectedDeviceIds([]);
      return;
    }
    refreshAvailability(eventDate);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [eventDate]);

  useEffect(() => subscribeBookingSelection((detail) => {
    if (detail?.selectedPackage) {
      setForm((current) => ({ ...current, selected_package: detail.selectedPackage }));
    }
    if (detail?.extraId && extraPresentation.some((item) => item.id === detail.extraId)) {
      setSelectedExtras((current) => current.includes(detail.extraId) ? current : [...current, detail.extraId]);
    }
  }), [extraPresentation]);

  useEffect(() => {
    if (!mobileSummary) return undefined;

    mobileSummaryPreviousFocusRef.current = document.activeElement;
    const focusFrame = window.requestAnimationFrame(() => {
      const dialog = mobileSummaryDialogRef.current;
      dialog?.querySelector("button:not([disabled]), a[href], input:not([disabled]), [tabindex]:not([tabindex='-1'])")?.focus?.();
      if (dialog && !dialog.contains(document.activeElement)) dialog.focus();
    });
    const handleKeyDown = (event) => {
      if (event.key === "Escape") {
        event.preventDefault();
        setMobileSummary(false);
        return;
      }
      trapTabKey(event, mobileSummaryDialogRef.current);
    };
    window.addEventListener("keydown", handleKeyDown);

    return () => {
      window.cancelAnimationFrame(focusFrame);
      window.removeEventListener("keydown", handleKeyDown);
      window.requestAnimationFrame(() => mobileSummaryPreviousFocusRef.current?.focus?.());
    };
  }, [mobileSummary]);

  const markBookingStarted = () => {
    if (step === 1) trackBookingEventOnce("booking_started", { booking_step: 1 });
  };

  const trackForwardStepCompletion = (nextStep) => {
    if (nextStep <= step) return;

    if (step <= 1 && nextStep >= 2 && eventDate) {
      trackBookingEventOnce("booking_started", { booking_step: 1 });
      trackBookingEventOnce("date_selected", {
        booking_step: 1,
        days_until_event: daysUntilEvent(eventDate, today),
      });
    }
    if (step <= 2 && nextStep >= 3 && selectedDeviceIds.length > 0) {
      trackBookingEventOnce("device_selected", {
        booking_step: 2,
        ...deviceAnalyticsProperties(selectedDevices),
      });
    }
    if (step <= 3 && nextStep >= 4 && selectedDeviceIds.length > 0) {
      trackBookingEventOnce("package_selected", {
        booking_step: 3,
        extras_count: selectedExtras.length,
        has_paid_extras: selectedExtras.length > 0,
      });
    }
  };

  const goToStep = (nextStep) => {
    setMobileSummary(false);
    trackForwardStepCompletion(nextStep);
    if (nextStep === 4 && step !== 4) {
      trackBookingEvent("booking_summary_viewed", {
        ...deviceAnalyticsProperties(selectedDevices),
        extras_count: selectedExtras.length,
        amount_huf: total,
        currency: "HUF",
        language: lang,
      });
    }
    setStep(nextStep);
  };

  const editSummary = (targetStep) => {
    const eventNames = {
      1: "booking_summary_edit_date",
      2: "booking_summary_edit_device",
      3: "booking_summary_edit_extras",
    };
    if (eventNames[targetStep]) trackBookingEvent(eventNames[targetStep]);
    goToStep(targetStep);
  };

  const selectDate = (date) => {
    if (startOfDay(date) < today) return;
    setEventDate(isoDate(date));
    setSelectedDeviceIds([]);
    setRequestId(newRequestId());
  };

  const toggleDevice = (device) => {
    if (!device.available) return;
    setSelectedDeviceIds((current) => current.includes(device.id) ? current.filter((id) => id !== device.id) : [...current, device.id]);
    setRequestId(newRequestId());
  };

  const toggleExtra = (id) => {
    setSelectedExtras((current) => current.includes(id) ? current.filter((value) => value !== id) : [...current, id]);
    setRequestId(newRequestId());
  };

  const handleChange = (event) => {
    const { name, value } = event.target;
    setForm((current) => {
      const next = { ...current, [name]: value };
      if (name === "customer_name" && !current.billing_name.trim()) next.billing_name = value;
      return next;
    });
    if (errors[name]) setErrors((current) => ({ ...current, [name]: "" }));
  };

  const validate = () => {
    const next = validationErrors(form, consent, terms, c);
    setErrors(next);
    if (Object.keys(next).length > 0) {
      toast.error(c.checkMissing);
      requestAnimationFrame(() => {
        const firstError = document.querySelector(`#${DETAILS_FORM_ID} [aria-invalid="true"]`);
        const prefersReducedMotion = window.matchMedia?.("(prefers-reduced-motion: reduce)")?.matches;
        firstError?.scrollIntoView({ behavior: prefersReducedMotion ? "auto" : "smooth", block: "center" });
        firstError?.focus?.({ preventScroll: true });
      });
      return false;
    }
    return true;
  };

  const advance = () => {
    if (step === 1) {
      if (!eventDate) {
        toast.error(c.pickDate);
        return;
      }
      trackBookingEventOnce("booking_started", { booking_step: 1 });
      trackBookingEventOnce("date_selected", {
        booking_step: 1,
        days_until_event: daysUntilEvent(eventDate, today),
      });
      goToStep(2);
      return;
    }
    if (step === 2) {
      if (loading) return;
      if (!selectedDeviceIds.length) {
        toast.error(c.pickDevice);
        return;
      }
      trackBookingEventOnce("device_selected", {
        booking_step: 2,
        ...deviceAnalyticsProperties(selectedDevices),
      });
      goToStep(3);
      return;
    }
    if (step === 3) {
      trackBookingEventOnce("package_selected", {
        booking_step: 3,
        extras_count: selectedExtras.length,
        has_paid_extras: selectedExtras.length > 0,
      });
      goToStep(4);
    }
  };

  const submit = async (event) => {
    event.preventDefault();
    if (submitLock.current || submitting) return;
    if (!validate()) return;
    if (!eventDate || selectedDeviceIds.length === 0) {
      goToStep(eventDate ? 2 : 1);
      toast.error(eventDate ? c.pickDevice : c.pickDate);
      return;
    }
    submitLock.current = true;
    setSubmitting(true);
    trackBookingEventOnce("details_completed", {
      booking_step: 4,
      form_valid: true,
    });

    try {
      const catalogResponse = await axios.get(`${API}/booking-catalog`);
      const authoritative = authoritativeCatalogTotal(catalogResponse.data, selectedDeviceIds, selectedExtras);
      if (!authoritative || authoritative.total !== total) {
        const nextCatalogExtras = Array.isArray(catalogResponse.data?.extras) ? catalogResponse.data.extras : [];
        const nextCatalogDevices = Array.isArray(catalogResponse.data?.devices) ? catalogResponse.data.devices : [];
        setCatalogExtras(nextCatalogExtras);
        setDevices((current) => current.map((device) => {
          const fresh = nextCatalogDevices.find((item) => String(item.id) === String(device.id));
          return fresh ? { ...device, ...fresh, id: String(fresh.id), available: device.available } : device;
        }));
        setRequestId(newRequestId());
        toast.error(c.priceChanged);
        return;
      }

      const { billing_name, billing_country, billing_postal_code, billing_city, billing_address, billing_tax_number, ...customer } = form;
      const booking = await axios.post(`${API}/bookings-v2`, {
        ...customer,
        event_date: eventDate,
        device_ids: selectedDeviceIds,
        request_id: requestId,
        selected_extras: selectedExtras,
        message: [
          form.message.trim(),
          `Kiválasztott Telefonkák: ${selectedDevices.map((device) => device.name).join(" + ")}`,
          `Fizetendő végösszeg: ${money(total, "hu")}`,
          `Feltételek elfogadva: ${new Date().toISOString()} (${POLICY_VERSION})`,
        ].filter(Boolean).join("\n\n"),
        billing: {
          name: billing_name.trim(),
          country: billing_country.trim(),
          postal_code: billing_postal_code.trim(),
          city: billing_city.trim(),
          address: billing_address.trim(),
          tax_number: billing_tax_number.trim() || null,
        },
        language: lang === "en" ? "en" : "hu",
      });

      const bookedTotal = Number(booking.data?.pricing?.total_gross_huf);
      if (!Number.isFinite(bookedTotal) || bookedTotal !== authoritative.total) {
        toast.error(c.priceVerificationFailed);
        return;
      }

      const checkout = await axios.post(`${API}/payments/stripe/checkout`, { booking_id: booking.data.id });
      if (!checkout.data?.url) throw new Error("missing_checkout_url");
      trackBookingEventOnce("checkout_started", {
        booking_step: 4,
        ...deviceAnalyticsProperties(authoritative.devices),
        extras_count: authoritative.extras.length,
        has_paid_extras: authoritative.extras.length > 0,
        amount_huf: bookedTotal,
        currency: "HUF",
      });
      window.location.assign(checkout.data.url);
    } catch (error) {
      if (error?.response?.status === 409 && String(error?.config?.url || "").includes("bookings-v2")) {
        toast.error(c.conflict);
        await refreshAvailability(eventDate);
        setRequestId(newRequestId());
        goToStep(2);
      } else {
        toast.error(c.generic);
      }
    } finally {
      submitLock.current = false;
      setSubmitting(false);
    }
  };

  const unlocked = (number) => number === 1 || (number === 2 && !!eventDate) || (number >= 3 && selectedDeviceIds.length > 0);

  return (
    <section
      id="booking"
      onPointerDownCapture={markBookingStarted}
      onKeyDownCapture={markBookingStarted}
      className="pt-20 pb-36 md:pt-28 md:pb-40 lg:py-28 bg-[var(--beige-100)] border-y border-[rgba(110,30,43,0.07)] tk-section-anchor overflow-x-clip"
    >
      <div className="max-w-7xl mx-auto px-5 md:px-10">
        <div className="text-center mb-9">
          <span className="tk-eyebrow"><span className="tk-divider" />{lang === "hu" ? "Foglalás" : "Booking"}<span className="tk-divider" /></span>
          <h2 className="font-serif text-4xl sm:text-5xl mt-5 text-[var(--ink)]">{c.title}</h2>
        </div>

        <div ref={stepTopRef} className="tk-card p-3 scroll-mt-24">
          <div className="grid grid-cols-4 gap-1.5 sm:gap-2">
            {c.steps.map((label, index) => {
              const number = index + 1;
              const active = step === number;
              const complete = step > number;
              const canOpen = unlocked(number);
              return (
                <button
                  key={label}
                  type="button"
                  disabled={!canOpen}
                  onClick={() => canOpen && goToStep(number)}
                  aria-current={active ? "step" : undefined}
                  className={`min-h-11 rounded-2xl px-1.5 sm:px-3 py-2.5 flex items-center justify-center gap-2 text-xs sm:text-sm transition ${active ? "bg-[var(--burgundy)] text-white shadow-md" : canOpen ? "bg-white text-[var(--ink)]" : "opacity-35"}`}
                >
                  <span className="w-7 h-7 shrink-0 rounded-full border border-current/20 flex items-center justify-center">
                    {complete ? <Check size={14} aria-hidden="true" /> : number}
                  </span>
                  <span className="hidden sm:block min-w-0 truncate">{label}</span>
                </button>
              );
            })}
          </div>
          <p className="sm:hidden mt-2 text-center text-xs text-[var(--ink-soft)]">{step}/4 · {c.steps[step - 1]}</p>
        </div>

        {step < 4 && (
          <CompactSelectionSummary
            c={c}
            lang={lang}
            eventDate={eventDate}
            devices={selectedDevices}
            selectedPackage={form.selected_package}
            extras={chosenExtras}
            total={total}
          />
        )}

        <div className="mt-6 lg:mt-8 grid lg:grid-cols-[minmax(0,1fr)_360px] gap-7 items-start">
          <div className="min-w-0">
            {step === 1 && (
              <CalendarStep
                c={c}
                lang={lang}
                cursor={cursor}
                setCursor={setCursor}
                days={calendarDays(cursor)}
                today={today}
                eventDate={eventDate}
                onSelect={selectDate}
                next={advance}
              />
            )}
            {step === 2 && (
              <DeviceStep
                c={c}
                lang={lang}
                devices={devices}
                selected={selectedDeviceIds}
                loading={loading}
                error={availabilityError}
                retry={() => refreshAvailability(eventDate)}
                toggle={toggleDevice}
                back={() => goToStep(1)}
                next={advance}
              />
            )}
            {step === 3 && (
              <ExtrasStep
                c={c}
                lang={lang}
                gifts={gifts}
                extras={extrasCatalog}
                selected={selectedExtras}
                toggle={toggleExtra}
                clear={() => setSelectedExtras([])}
                back={() => goToStep(2)}
                next={advance}
              />
            )}
            {step === 4 && (
              <DetailsStep
                c={c}
                lang={lang}
                form={form}
                errors={errors}
                change={handleChange}
                consent={consent}
                setConsent={(value) => { setConsent(value); if (errors.consent) setErrors((current) => ({ ...current, consent: "" })); }}
                terms={terms}
                setTerms={(value) => { setTerms(value); if (errors.terms) setErrors((current) => ({ ...current, terms: "" })); }}
                submitting={submitting}
                submit={submit}
                back={() => goToStep(3)}
                ready={detailsReady}
                eventDate={eventDate}
                devices={selectedDevices}
                selectedPackage={form.selected_package}
                extras={chosenExtras}
                gifts={gifts}
                total={total}
                edit={editSummary}
              />
            )}
          </div>

          <Summary
            c={c}
            lang={lang}
            eventDate={eventDate}
            devices={selectedDevices}
            selectedPackage={form.selected_package}
            extras={chosenExtras}
            gifts={gifts}
            total={total}
            edit={editSummary}
            checkoutMode={step === 4}
          />
        </div>
      </div>

      {mobileSummary && (
        <div className="lg:hidden fixed inset-0 z-40 bg-black/20" onClick={() => setMobileSummary(false)} role="presentation">
          <div
            id={MOBILE_SUMMARY_ID}
            ref={mobileSummaryDialogRef}
            tabIndex={-1}
            className="absolute inset-x-3 bottom-[calc(6.75rem+env(safe-area-inset-bottom))] max-h-[65dvh] overflow-y-auto rounded-[22px] focus:outline-none"
            onClick={(event) => event.stopPropagation()}
            role="dialog"
            aria-modal="true"
            aria-label={c.summary}
          >
            <button
              type="button"
              onClick={() => setMobileSummary(false)}
              aria-label={c.closeSummary}
              className="absolute top-3 right-3 z-20 w-11 h-11 rounded-full bg-white text-[var(--burgundy)] border border-[rgba(110,30,43,0.15)] shadow-sm inline-flex items-center justify-center"
            >
              <X size={18} aria-hidden="true" />
            </button>
            <Summary
              c={c}
              lang={lang}
              eventDate={eventDate}
              devices={selectedDevices}
              selectedPackage={form.selected_package}
              extras={chosenExtras}
              gifts={gifts}
              total={total}
              edit={editSummary}
              mobile
              checkoutMode={step === 4}
            />
          </div>
        </div>
      )}

      <MobileActionBar
        c={c}
        lang={lang}
        step={step}
        total={total}
        ready={stepReady}
        busy={loading || submitting}
        summaryOpen={mobileSummary}
        summaryTriggerRef={mobileSummaryTriggerRef}
        openSummary={() => setMobileSummary((value) => !value)}
        advance={advance}
      />
    </section>
  );
}

function CompactSelectionSummary({ c, lang, eventDate, devices, selectedPackage, extras, total }) {
  const hasSelection = eventDate || devices.length || selectedPackage || extras.length;
  if (!hasSelection) return null;

  return (
    <div className="lg:hidden mt-4 rounded-2xl border border-[rgba(110,30,43,0.10)] bg-white/75 px-4 py-3">
      <div className="flex flex-wrap items-center gap-x-4 gap-y-1.5 text-xs text-[var(--ink-soft)]">
        {eventDate && <span><strong className="text-[var(--ink)] font-medium">{c.date}:</strong> {dateLabel(eventDate, lang)}</span>}
        {devices.length > 0 && <span><strong className="text-[var(--ink)] font-medium">{c.devices}:</strong> {devices.map((device) => device.name).join(" + ")}</span>}
        {selectedPackage && <span><strong className="text-[var(--ink)] font-medium">{c.package}:</strong> {selectedPackage}</span>}
        {extras.length > 0 && <span><strong className="text-[var(--ink)] font-medium">{c.extras}:</strong> {extras.length}</span>}
        {devices.length > 0 && <span className="font-medium text-[var(--burgundy)]">{c.total}: {money(total, lang)}</span>}
      </div>
    </div>
  );
}

function CalendarStep({ c, lang, cursor, setCursor, days, today, eventDate, onSelect, next }) {
  const week = lang === "hu" ? ["H", "K", "Sze", "Cs", "P", "Szo", "V"] : ["M", "T", "W", "T", "F", "S", "S"];
  const month = new Intl.DateTimeFormat(lang === "hu" ? "hu-HU" : "en-GB", { month: "long", year: "numeric" }).format(cursor);

  return (
    <div className="tk-card p-5 sm:p-8">
      <p className="text-xs uppercase tracking-[0.18em] text-[var(--burgundy)]">01 · {c.steps[0]}</p>
      <h3 className="font-serif text-3xl mt-2">{c.calendarTitle}</h3>
      <p className="mt-2 text-sm text-[var(--ink-soft)]">{c.calendarBody}</p>

      <div className="mt-7 rounded-[28px] border bg-white p-3 sm:p-5">
        <div className="flex items-center justify-between">
          <button type="button" aria-label={c.previousMonth} className="w-11 h-11 rounded-full border flex items-center justify-center" onClick={() => setCursor(new Date(cursor.getFullYear(), cursor.getMonth() - 1, 1))}><ChevronLeft size={18} aria-hidden="true" /></button>
          <h4 className="font-serif text-xl sm:text-2xl capitalize" aria-live="polite">{month}</h4>
          <button type="button" aria-label={c.nextMonth} className="w-11 h-11 rounded-full border flex items-center justify-center" onClick={() => setCursor(new Date(cursor.getFullYear(), cursor.getMonth() + 1, 1))}><ChevronRight size={18} aria-hidden="true" /></button>
        </div>
        <div className="grid grid-cols-7 mt-4">{week.map((day, index) => <span key={`${day}-${index}`} className="text-center text-[10px] uppercase text-[var(--ink-soft)] py-2">{day}</span>)}</div>
        <div className="grid grid-cols-7 gap-1 sm:gap-2">
          {days.map((date) => {
            const id = isoDate(date);
            const past = startOfDay(date) < today;
            const outside = date.getMonth() !== cursor.getMonth();
            const selected = id === eventDate;
            return (
              <button
                key={id}
                type="button"
                disabled={past}
                aria-pressed={selected}
                aria-label={dateLabel(id, lang)}
                onClick={() => onSelect(date)}
                className={`aspect-square min-h-11 rounded-xl sm:rounded-2xl text-sm transition ${selected ? "bg-[var(--burgundy)] text-white shadow-lg ring-2 ring-[rgba(110,30,43,0.18)] ring-offset-2" : past ? "opacity-20" : outside ? "opacity-40 hover:bg-[rgba(110,30,43,0.06)]" : "hover:bg-[rgba(110,30,43,0.08)]"}`}
              >
                {date.getDate()}
              </button>
            );
          })}
        </div>
      </div>

      {eventDate && (
        <div className="mt-6 rounded-2xl border border-[rgba(110,30,43,0.12)] bg-[rgba(110,30,43,0.06)] p-5 flex items-center gap-3">
          <span className="w-8 h-8 shrink-0 rounded-full bg-[var(--burgundy)] text-white flex items-center justify-center"><Check size={15} aria-hidden="true" /></span>
          <div>
            <p className="text-xs uppercase tracking-widest text-[var(--burgundy)]">{c.selectedDate}</p>
            <p className="font-serif text-xl mt-1">{dateLabel(eventDate, lang)}</p>
          </div>
        </div>
      )}

      <Nav c={c} step={1} next={next} nextReady={!!eventDate} />
    </div>
  );
}

function DeviceStep({ c, lang, devices, selected, loading, error, retry, toggle, back, next }) {
  return (
    <div className="tk-card p-5 sm:p-8">
      <p className="text-xs uppercase tracking-[0.18em] text-[var(--burgundy)]">02 · {c.steps[1]}</p>
      <h3 className="font-serif text-3xl mt-2">{c.phoneTitle}</h3>
      <p className="mt-2 text-sm text-[var(--ink-soft)]">{c.phoneBody}</p>

      {loading && <div className="py-14 text-center text-[var(--ink-soft)]" role="status" aria-live="polite">{c.loadingAvailability}</div>}

      {!loading && error && (
        <div className="mt-7 rounded-2xl border border-[rgba(110,30,43,0.20)] bg-[rgba(110,30,43,0.05)] p-5 text-center" role="alert">
          <AlertTriangle className="mx-auto text-[var(--burgundy)]" size={24} aria-hidden="true" />
          <p className="mt-3 text-sm text-[var(--ink-soft)]">{c.availabilityError}</p>
          <button type="button" onClick={retry} className="tk-btn-secondary justify-center mt-4">{c.retry}</button>
        </div>
      )}

      {!loading && !error && (
        <div className="mt-7 grid sm:grid-cols-2 gap-4">
          {devices.map((device) => {
            const chosen = selected.includes(device.id);
            const unavailable = !device.available;
            return (
              <button
                key={device.id}
                type="button"
                disabled={unavailable}
                aria-pressed={chosen}
                onClick={() => toggle(device)}
                className={`overflow-hidden text-left rounded-[24px] border-2 transition ${unavailable ? "opacity-55 bg-white cursor-not-allowed border-black/5" : chosen ? "border-[var(--burgundy)] bg-[rgba(110,30,43,0.04)] shadow-lg ring-2 ring-[rgba(110,30,43,0.08)]" : "border-black/10 bg-white hover:border-[rgba(110,30,43,0.35)]"}`}
              >
                <div className="h-40 bg-[var(--beige-50)] flex items-center justify-center overflow-hidden">
                  {device.image_url ? <img src={device.image_url} alt="" className="w-full h-full object-contain p-4" /> : <Phone size={58} className="text-[var(--burgundy)] opacity-80" aria-hidden="true" />}
                </div>
                <div className="p-5">
                  <div className="flex items-start justify-between gap-3">
                    <div>
                      <h4 className="font-serif text-xl">{device.name}</h4>
                      <p className={`mt-1 text-xs uppercase tracking-widest ${unavailable ? "text-[var(--burgundy)]" : "text-[var(--ink-soft)]"}`}>{unavailable ? c.booked : c.available}</p>
                    </div>
                    {chosen && <span className="w-7 h-7 shrink-0 rounded-full bg-[var(--burgundy)] text-white flex items-center justify-center"><Check size={14} aria-hidden="true" /></span>}
                  </div>
                  <p className="font-serif text-2xl mt-5 text-[var(--burgundy)]">{money(device.price, lang)}</p>
                  <span className={`mt-5 min-h-11 rounded-full px-4 inline-flex items-center justify-center text-sm font-medium ${chosen ? "bg-[var(--burgundy)] text-white" : "border"}`}>{unavailable ? c.booked : chosen ? c.chosen : c.choose}</span>
                </div>
              </button>
            );
          })}
        </div>
      )}

      {!loading && !error && devices.length === 0 && <p className="py-10 text-center text-sm text-[var(--ink-soft)]">{c.availabilityError}</p>}
      <div className="mt-5 rounded-2xl bg-[rgba(110,30,43,0.05)] p-4 text-sm text-[var(--ink-soft)]">{c.bothHint}</div>
      <Nav c={c} step={2} back={back} next={next} nextReady={selected.length > 0 && !loading && !error} />
    </div>
  );
}

function ExtrasStep({ c, lang, gifts, extras, selected, toggle, clear, back, next }) {
  return (
    <div className="tk-card p-5 sm:p-8">
      <p className="text-xs uppercase tracking-[0.18em] text-[var(--burgundy)]">03 · {c.steps[2]}</p>
      <h3 className="font-serif text-3xl mt-2">{c.extrasTitle}</h3>
      <p className="mt-2 text-sm text-[var(--ink-soft)]">{c.extrasBody}</p>

      <div className="mt-7 rounded-[26px] border border-[rgba(110,30,43,0.20)] bg-[rgba(110,30,43,0.045)] p-5">
        <h4 className="font-serif text-2xl">{c.giftsTitle}</h4>
        <div className="mt-4 grid gap-3">
          {gifts.map((gift) => {
            const Icon = gift.icon;
            return (
              <div key={gift.name} className="rounded-2xl bg-white p-4 flex items-center gap-3">
                <span className="w-9 h-9 shrink-0 rounded-full bg-[rgba(110,30,43,0.08)] text-[var(--burgundy)] flex items-center justify-center"><Icon size={16} aria-hidden="true" /></span>
                <span>{gift.name}</span>
                <span className="ml-auto text-xs uppercase text-[var(--burgundy)]">{c.gift}</span>
              </div>
            );
          })}
        </div>
      </div>

      <h4 className="font-serif text-2xl mt-8">{c.extrasPaid}</h4>
      <div className="mt-4 grid gap-3">
        {extras.map((item) => {
          const Icon = item.icon;
          const chosen = selected.includes(item.id);
          return (
            <button
              key={item.id}
              type="button"
              aria-pressed={chosen}
              onClick={() => toggle(item.id)}
              className={`min-h-11 rounded-2xl border-2 p-5 flex items-center gap-4 text-left transition ${chosen ? "border-[var(--burgundy)] bg-[rgba(110,30,43,0.04)] shadow-sm" : "border-black/10 bg-white"}`}
            >
              <span className="w-11 h-11 shrink-0 rounded-full bg-[var(--beige-100)] text-[var(--burgundy)] flex items-center justify-center"><Icon size={19} aria-hidden="true" /></span>
              <span className="flex-1 min-w-0"><span className="font-medium">{item.name}</span><span className="block font-serif text-lg text-[var(--burgundy)] mt-1">{money(item.price, lang)}</span></span>
              {chosen && <span className="w-7 h-7 shrink-0 rounded-full bg-[var(--burgundy)] text-white flex items-center justify-center"><Check size={14} aria-hidden="true" /></span>}
            </button>
          );
        })}
      </div>

      <button type="button" onClick={clear} className={`mt-4 w-full min-h-11 rounded-2xl border p-4 text-sm ${selected.length === 0 ? "border-[var(--burgundy)] text-[var(--burgundy)] bg-[rgba(110,30,43,0.03)]" : ""}`}>
        {selected.length === 0 && <Check size={14} className="inline mr-2" aria-hidden="true" />}{c.noExtras}
      </button>
      <Nav c={c} step={3} back={back} next={next} nextReady />
    </div>
  );
}

function DetailsStep({ c, lang, form, errors, change, consent, setConsent, terms, setTerms, submitting, submit, back, ready, eventDate, devices, selectedPackage, extras, gifts, total, edit }) {
  const hasErrors = Object.values(errors).some(Boolean);
  return (
    <form id={DETAILS_FORM_ID} onSubmit={submit} className="tk-card p-5 sm:p-8" noValidate>
      <p className="text-xs uppercase tracking-[0.18em] text-[var(--burgundy)]">04 · {c.steps[3]}</p>
      <h3 className="font-serif text-3xl mt-2">{c.detailsTitle}</h3>
      <p className="mt-2 text-sm text-[var(--ink-soft)]">{c.detailsBody}</p>
      <p className="sr-only" role="alert" aria-live="assertive">{hasErrors ? c.checkMissing : ""}</p>

      <div className="mt-7 rounded-2xl bg-[rgba(110,30,43,0.045)] px-4 py-3">
        <p className="text-xs uppercase tracking-[0.16em] text-[var(--burgundy)]">{c.detailsSection}</p>
      </div>

      <div className="mt-5 grid sm:grid-cols-2 gap-4">
        <Field label={c.name} name="customer_name" value={form.customer_name} change={change} error={errors.customer_name} autoComplete="name" />
        <Field label={c.email} name="customer_email" type="email" value={form.customer_email} change={change} error={errors.customer_email} icon={<Mail size={13} />} autoComplete="email" inputMode="email" />
        <Field label={c.phone} name="customer_phone" type="tel" value={form.customer_phone} change={change} error={errors.customer_phone} icon={<Phone size={13} />} autoComplete="tel" inputMode="tel" />
        <Field label={c.location} name="event_location" value={form.event_location} change={change} error={errors.event_location} icon={<MapPin size={13} />} />
      </div>

      <div className="mt-8 pt-7 border-t">
        <h4 className="font-serif text-2xl">{c.billingTitle}</h4>
        <div className="mt-5 grid sm:grid-cols-2 gap-4">
          <Field label={c.billingName} name="billing_name" value={form.billing_name} change={change} error={errors.billing_name} autoComplete="organization" />
          <Field label={c.country} name="billing_country" value={form.billing_country} change={change} error={errors.billing_country} autoComplete="country-name" />
          <Field label={c.postal} name="billing_postal_code" value={form.billing_postal_code} change={change} error={errors.billing_postal_code} autoComplete="postal-code" inputMode="numeric" />
          <Field label={c.city} name="billing_city" value={form.billing_city} change={change} error={errors.billing_city} autoComplete="address-level2" />
          <div className="sm:col-span-2"><Field label={c.address} name="billing_address" value={form.billing_address} change={change} error={errors.billing_address} autoComplete="street-address" /></div>
          <div className="sm:col-span-2"><Field label={c.tax} name="billing_tax_number" value={form.billing_tax_number} change={change} autoComplete="off" /></div>
        </div>
      </div>

      <label className="block mt-6" htmlFor="booking-message">
        <span className="text-xs uppercase tracking-widest text-[var(--ink-soft)]">{c.note}</span>
        <textarea id="booking-message" name="message" rows={4} value={form.message} onChange={change} className="tk-input resize-none mt-2" />
      </label>

      <CheckLabel name="consent" checked={consent} set={setConsent} text={c.consent} error={errors.consent} />
      <CheckLabel name="terms" checked={terms} set={setTerms} text={c.terms} error={errors.terms} />

      <div className="lg:hidden mt-8 pt-7 border-t" data-testid="checkout-mobile-summary">
        <p className="text-xs uppercase tracking-[0.16em] text-[var(--burgundy)]">{c.summarySection}</p>
        <div className="mt-3 rounded-2xl border border-[rgba(110,30,43,0.12)] bg-white overflow-hidden">
          <div className="px-4 py-4 border-b border-[rgba(110,30,43,0.08)]">
            <p className="text-xs text-[var(--ink-soft)]">{shortDateLabel(eventDate, lang)}</p>
            <p className="font-serif text-lg mt-1">{devices.map((device) => device.name).join(" + ")}</p>
            <div className="mt-2 flex items-center justify-between gap-3 text-xs text-[var(--ink-soft)]">
              <span>{extras.length} {c.extras.toLocaleLowerCase()}</span>
              <strong className="font-serif text-xl text-[var(--burgundy)]">{money(total, lang)}</strong>
            </div>
          </div>
          <details>
            <summary className="min-h-11 cursor-pointer px-4 py-3 flex items-center justify-between gap-3 text-sm font-medium text-[var(--burgundy)] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-[-2px] focus-visible:outline-[var(--burgundy)]">
              {c.viewDetails}<ChevronDown size={16} aria-hidden="true" />
            </summary>
            <div className="px-4 pb-5">
              <CheckoutSummaryContent c={c} lang={lang} eventDate={eventDate} devices={devices} selectedPackage={selectedPackage} extras={extras} gifts={gifts} total={total} edit={edit} showGifts />
            </div>
          </details>
        </div>
      </div>

      <div className="mt-7 rounded-2xl border border-[rgba(110,30,43,0.14)] bg-[rgba(110,30,43,0.04)] p-4">
        <div className="flex items-start gap-3">
          <span className="w-9 h-9 shrink-0 rounded-full bg-white text-[var(--burgundy)] flex items-center justify-center"><Lock size={16} aria-hidden="true" /></span>
          <div>
            <p className="font-medium text-sm">{c.reviewBeforePayment}</p>
            <p className="mt-1 text-sm text-[var(--ink-soft)]">{c.stripeNext}</p>
          </div>
        </div>
        <p className="mt-3 pt-3 border-t border-[rgba(110,30,43,0.10)] flex items-start gap-2 text-xs text-[var(--ink-soft)]"><Lock size={13} className="mt-0.5 shrink-0" aria-hidden="true" />{c.stripeTrust}</p>
      </div>

      <div className="hidden lg:flex mt-7 gap-3">
        <button type="button" onClick={back} disabled={submitting} className="tk-btn-secondary justify-center"><ChevronLeft size={16} aria-hidden="true" />{c.back}</button>
        <button type="submit" disabled={submitting} aria-disabled={!ready || submitting} className={`tk-btn-primary justify-center flex-1 disabled:opacity-40 ${ready ? "" : "opacity-60"}`}>{submitting ? c.paying : c.pay}<Lock size={15} aria-hidden="true" /></button>
      </div>
    </form>
  );
}

function CheckoutSummaryContent({ c, lang, eventDate, devices, selectedPackage, extras, gifts, total, edit, showGifts = false }) {
  return (
    <>
      <SummaryRow label={c.date} value={dateLabel(eventDate, lang)} edit={() => edit(1)} editLabel={c.edit} />
      <SummaryRow label={c.devices} value={devices.length ? devices.map((device) => device.name).join(" + ") : "—"} edit={() => edit(2)} editLabel={c.edit} />
      {selectedPackage && <SummaryRow label={c.package} value={selectedPackage} edit={() => edit(3)} editLabel={c.edit} />}

      <div className="mt-5">
        <div className="flex items-center justify-between gap-3">
          <p className="text-[10px] uppercase tracking-widest text-[var(--ink-soft)]">{c.extras}</p>
          <button type="button" onClick={() => edit(3)} aria-label={`${c.edit}: ${c.extras}`} className="min-h-11 min-w-11 px-2 inline-flex items-center justify-center text-xs text-[var(--burgundy)] underline focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[var(--burgundy)]">{c.edit}</button>
        </div>
        {extras.length ? (
          <div className="space-y-2">
            {extras.map((item) => <Price key={item.id} label={item.name} value={money(item.price, lang)} />)}
          </div>
        ) : <p className="text-sm text-[var(--ink-soft)]">{c.noExtrasSummary}</p>}
      </div>

      {showGifts && (
        <div className="mt-6 rounded-2xl bg-[rgba(110,30,43,0.045)] p-4">
          <p className="text-[10px] uppercase tracking-widest text-[var(--burgundy)]">{c.included}</p>
          <div className="mt-3 space-y-2.5">
            {gifts.map((gift) => (
              <div key={gift.name} className="flex items-center gap-2 text-sm">
                <span className="w-6 h-6 shrink-0 rounded-full bg-white text-[var(--burgundy)] flex items-center justify-center"><Check size={12} aria-hidden="true" /></span>
                <span className="min-w-0 flex-1">{gift.name}</span>
                <span className="text-[10px] uppercase tracking-wider text-[var(--burgundy)]">{c.gift}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="mt-6 pt-5 border-t space-y-3 text-sm">
        {devices.map((device) => <Price key={device.id} label={device.name} value={money(device.price, lang)} />)}
        {extras.map((item) => <Price key={`total-${item.id}`} label={item.name} value={money(item.price, lang)} />)}
        <div className="pt-4 mt-2 border-t border-[rgba(110,30,43,0.16)] flex justify-between items-end gap-4">
          <span className="font-medium">{c.total}</span>
          <span className="font-serif text-3xl text-[var(--burgundy)]">{money(total, lang)}</span>
        </div>
      </div>
    </>
  );
}

function Summary({ c, lang, eventDate, devices, selectedPackage, extras, gifts, total, edit, mobile = false, checkoutMode = false }) {
  return (
    <aside className={`${mobile ? "" : "hidden lg:block sticky top-24"} tk-card p-6 shadow-xl`}>
      {checkoutMode && <p className="text-xs uppercase tracking-[0.16em] text-[var(--burgundy)] mb-2">{c.summarySection}</p>}
      <h3 className="font-serif text-2xl">{c.summary}</h3>
      <CheckoutSummaryContent c={c} lang={lang} eventDate={eventDate} devices={devices} selectedPackage={selectedPackage} extras={extras} gifts={gifts} total={total} edit={edit} showGifts={checkoutMode} />
    </aside>
  );
}

function MobileActionBar({ c, lang, step, total, ready, busy, summaryOpen, summaryTriggerRef, openSummary, advance }) {
  const label = busy ? (step === 4 ? c.paying : c.loadingAvailability) : c.cta[step - 1];
  const handleClick = () => {
    if (step < 4) advance();
  };

  return (
    <div data-testid="booking-flow-mobile-cta" className="lg:hidden fixed inset-x-0 bottom-0 z-30 border-t border-[rgba(110,30,43,0.10)] bg-[rgba(250,246,239,0.96)] backdrop-blur-md px-4 pt-3 pb-[calc(0.75rem+env(safe-area-inset-bottom))] shadow-[0_-12px_30px_rgba(44,31,26,0.10)]">
      <div className="max-w-2xl mx-auto flex items-center gap-3">
        <button
          ref={summaryTriggerRef}
          type="button"
          onClick={openSummary}
          className="min-h-11 min-w-11 min-w-0 flex-1 text-left rounded-xl px-2 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[var(--burgundy)]"
          aria-label={`${c.summaryToggle}: ${money(total, lang)}`}
          aria-expanded={summaryOpen}
          aria-controls={MOBILE_SUMMARY_ID}
        >
          <span className="block text-[10px] uppercase tracking-widest text-[var(--ink-soft)]">{step === 4 ? c.payable : c.total}</span>
          <span className="flex items-center gap-1.5 font-serif text-lg text-[var(--burgundy)]"><span className="truncate">{money(total, lang)}</span><ChevronDown size={14} aria-hidden="true" /></span>
        </button>
        <button
          type={step === 4 ? "submit" : "button"}
          form={step === 4 ? DETAILS_FORM_ID : undefined}
          onClick={handleClick}
          disabled={busy}
          aria-disabled={!ready || busy}
          className={`min-h-11 min-w-11 max-w-[62%] rounded-full px-4 sm:px-6 inline-flex items-center justify-center gap-1.5 text-sm font-medium transition focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[var(--burgundy)] ${ready && !busy ? "bg-[var(--burgundy)] text-white shadow-md" : "bg-[rgba(110,30,43,0.13)] text-[var(--burgundy)]"}`}
        >
          <span className="truncate">{label}</span>{step === 4 ? <Lock size={14} aria-hidden="true" /> : <ChevronRight size={15} aria-hidden="true" />}
        </button>
      </div>
    </div>
  );
}

function Field({ label, name, value, change, error, icon, type = "text", autoComplete, inputMode }) {
  const inputId = `booking-${name}`;
  const errorId = `${inputId}-error`;
  return (
    <div className="block">
      <label htmlFor={inputId} className="flex items-center gap-1 text-xs uppercase tracking-widest text-[var(--ink-soft)] mb-2">
        {icon && <span aria-hidden="true">{icon}</span>}{label}
      </label>
      <input
        id={inputId}
        className={`tk-input ${error ? "border-[var(--burgundy)]" : ""}`}
        name={name}
        type={type}
        value={value}
        onChange={change}
        autoComplete={autoComplete}
        inputMode={inputMode}
        aria-invalid={error ? "true" : undefined}
        aria-describedby={error ? errorId : undefined}
      />
      {error && <p id={errorId} className="mt-1.5 text-sm text-[var(--burgundy)]">{error}</p>}
    </div>
  );
}

function CheckLabel({ name, checked, set, text, error }) {
  const inputId = `booking-${name}`;
  const errorId = `${inputId}-error`;
  return (
    <div className="mt-3">
      <label htmlFor={inputId} className={`min-h-11 flex items-start gap-3 rounded-2xl border p-4 cursor-pointer ${error ? "border-[var(--burgundy)]" : checked ? "border-[rgba(110,30,43,0.35)] bg-[rgba(110,30,43,0.03)]" : ""}`}>
        <input
          id={inputId}
          type="checkbox"
          checked={checked}
          onChange={(event) => set(event.target.checked)}
          aria-invalid={error ? "true" : undefined}
          aria-describedby={error ? errorId : undefined}
          className="mt-1 w-5 h-5 shrink-0 accent-[var(--burgundy)] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[var(--burgundy)]"
        />
        <span className="text-sm text-[var(--ink-soft)]">{text}</span>
      </label>
      {error && <p id={errorId} className="mt-1.5 text-sm text-[var(--burgundy)]">{error}</p>}
    </div>
  );
}

function Nav({ back, next, nextReady, c, step }) {
  return (
    <div className="hidden lg:flex mt-7 gap-3">
      {back && <button type="button" onClick={back} className="tk-btn-secondary justify-center"><ChevronLeft size={16} aria-hidden="true" />{c.back}</button>}
      <button
        type="button"
        onClick={next}
        aria-disabled={!nextReady}
        className={`tk-btn-primary justify-center flex-1 ${nextReady ? "" : "opacity-40"}`}
      >
        {c.cta[step - 1]}<ChevronRight size={16} aria-hidden="true" />
      </button>
    </div>
  );
}

function SummaryRow({ label, value, edit, editLabel }) {
  return (
    <div className="mt-5">
      <p className="text-[10px] uppercase tracking-widest text-[var(--ink-soft)]">{label}</p>
      <p className="text-sm mt-1 break-words">{value}</p>
      <button type="button" onClick={edit} aria-label={`${editLabel}: ${label}`} className="min-h-11 min-w-11 px-2 inline-flex items-center justify-center text-xs text-[var(--burgundy)] underline focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[var(--burgundy)]">{editLabel}</button>
    </div>
  );
}

function Price({ label, value }) {
  return <div className="flex justify-between gap-4"><span className="min-w-0 break-words">{label}</span><span className="font-medium text-right whitespace-nowrap">{value}</span></div>;
}
